// src/sentry.ts
import { appMode, sentryDsn } from "@constants";
import * as Sentry from "@sentry/react";

if (appMode !== "local" && sentryDsn) {
  // Limit capture to first-party
  const ALLOW_URLS = [
    /^https:\/\/dev-arkprj\.com\/?/,
    /^https:\/\/dev\.theark\.co\/?/,
    /^https:\/\/dev\.api\.theark\.co\/?/,
    /^https:\/\/theark\.co\/?/,
    /^https:\/\/api\.theark\.co\/?/,
  ];
  const DENY_URLS = [
    /extensions\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-extension:\/\//i,
  ];

  const REDACTED = "[REDACTED]";
  const scrubPatterns: RegExp[] = [
    /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, // email
    /(bearer\s+[a-z0-9._-]+|eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9._-]+\.[A-Za-z0-9._-]+)/gi, // jwt-ish
    /\+?\d[\d\s().-]{7,}\d/g, // phone-ish
    /(x-api-key|api[_-]?key|token)=?[A-Za-z0-9._-]{6,}/gi, // keys/tokens
    /0x[a-fA-F0-9]{40}/g, // wallet (optional PII)
  ];

  // Redact a definite string
  const redactString = (v: string): string =>
    scrubPatterns.reduce((acc, rx) => acc.replace(rx, REDACTED), v);

  // Best-effort deep redact via JSON round-trip (keeps types where possible)
  function redactData<T>(data: T): T {
    try {
      const s = JSON.stringify(data);
      return JSON.parse(redactString(s));
    } catch {
      return data;
    }
  }

  Sentry.init({
    dsn: sentryDsn,
    environment: appMode,

    // GDPR: do not send PII by default
    sendDefaultPii: false,

    allowUrls: ALLOW_URLS,
    denyUrls: DENY_URLS,

    // Disable Replay & Tracing until consent is added
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 0.0,
    tracesSampleRate: 0.0,
    tracePropagationTargets: ALLOW_URLS,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],

    beforeSend(event) {
      // Drop aborted Axios noise
      const aborted = event.exception?.values?.some(
        (e) =>
          e.type === "AxiosError" &&
          e.value?.toLowerCase().includes("request aborted")
      );
      if (aborted) return null;

      // Ensure no user/IP
      if (event.user) event.user = undefined;

      // Scrub request (headers/cookies/body/query/hash)
      if (event.request) {
        const req = event.request;

        if (req.headers) {
          const headers = { ...req.headers } as Record<string, unknown>;
          [
            "authorization",
            "cookie",
            "set-cookie",
            "x-api-key",
            "x-auth-token",
            "x-csrf-token",
          ].forEach((h) => {
            if (headers[h]) headers[h] = REDACTED;
          });
          req.headers = headers as Record<string, string>;
        }

        if (req.cookies) req.cookies = undefined;
        if (req.data) req.data = undefined;

        if (req.url) {
          try {
            const u = new URL(req.url);
            u.search = ""; // drop query
            u.hash = "";
            req.url = u.toString();
          } catch {
            /* ignore malformed */
          }
        }
      }

      // Scrub breadcrumbs (guard optional fields to satisfy TS)
      if (event.breadcrumbs?.length) {
        event.breadcrumbs = event.breadcrumbs.map((b): Sentry.Breadcrumb => {
          const message =
            typeof b.message === "string" ? redactString(b.message) : b.message; // string | undefined OK
          const data = b.data ? redactData(b.data) : undefined;
          return { ...b, message, data };
        });
      }

      // Scrub exception messages
      if (event.exception?.values?.length) {
        event.exception.values = event.exception.values.map((v) => ({
          ...v,
          value: typeof v.value === "string" ? redactString(v.value) : v.value,
        }));
      }

      return event;
    },

    beforeBreadcrumb(b) {
      if (typeof b.message === "string") b.message = redactString(b.message);
      if (b.data) b.data = redactData(b.data);
      return b;
    },
  });
} else {
  // eslint-disable-next-line no-console
  console.log("Sentry is disabled in local mode or DSN missing");
}
